import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AttachmentField, AttachmentItem, FormAutocomplete } from '../../design-system';

export type AbsenceEvidenceDocument = {
  id: string;
  displayName: string;
  subjectType: string;
  subjectId: string;
  subjectDescriptor?: string;
  scanStatus: string;
};
export type AbsenceEvidenceSelection = {
  workerId: string;
  documentIds: string[];
  documents: AbsenceEvidenceDocument[];
  ready: boolean;
  message?: string;
};
export type AbsenceEvidencePort = {
  list(workerId: string, personId?: string): Promise<AbsenceEvidenceDocument[]>;
  upload(workerId: string, file: File): Promise<{ id: string }>;
};

export function AbsenceEvidenceField({ port, workerId, personId, value, onChange, onPendingChange, onSelectionStateChange, canRead, canUpload, disabled, required, fixtureMode, error: invalid, helperText, onBlur, refreshKey }: {
  port?: AbsenceEvidencePort;
  workerId: string;
  personId?: string;
  value: string[];
  onChange: (ids: string[]) => void;
  onPendingChange?: (pending: boolean) => void;
  onSelectionStateChange?: (selection: AbsenceEvidenceSelection) => void;
  canRead: boolean;
  canUpload: boolean;
  disabled: boolean;
  required?: boolean;
  fixtureMode: boolean;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  /** 진행 중인 첨부를 유지하면서 문서 목록과 선택 가능 여부를 다시 확인합니다. */
  refreshKey?: number;
}) {
  const unavailableMessageId = useId();
  const [documents, setDocuments] = useState<AbsenceEvidenceDocument[]>([]);
  const [documentsContext, setDocumentsContext] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadStatus, setUploadStatus] = useState<{ message: string; severity: 'info' | 'success' } | null>(null);
  const [pendingUploads, setPendingUploads] = useState<string[]>([]);
  const pendingUploadNames = useRef(new Map<string, string>());
  const selection = useRef(value);
  const notifySelection = useRef(onChange);
  const notifyPending = useRef(onPendingChange);
  const notifySelectionState = useRef(onSelectionStateChange);
  const pending = useRef(false);
  selection.current = value;
  notifySelection.current = onChange;
  notifyPending.current = onPendingChange;
  notifySelectionState.current = onSelectionStateChange;
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const uploadInFlight = useRef(false);
  const uploadGeneration = useRef(0);
  const contextGeneration = useRef(0);
  const mounted = useRef(false);
  const contextKey = JSON.stringify([workerId, personId, fixtureMode, canRead]);
  const knownDocuments = useRef<{ context: string; values: Map<string, AbsenceEvidenceDocument> }>({ context: contextKey, values: new Map() });
  const activeContext = useRef(contextKey);
  activeContext.current = contextKey;
  const updatePending = useCallback((next: boolean) => {
    if (pending.current === next) return;
    pending.current = next;
    notifyPending.current?.(next);
  }, []);

  useEffect(() => { onPendingChange?.(pending.current); }, [onPendingChange]);
  useEffect(() => {
    mounted.current = true;
    pendingUploadNames.current.clear();
    uploadInFlight.current = false;
    knownDocuments.current = { context: contextKey, values: new Map() };
    setDocuments([]); setDocumentsContext(undefined); setLoading(false); setUploading(false);
    setPendingUploads([]); setUploadStatus(null); setUploadError(''); setError('');
    updatePending(false);
    return () => {
      mounted.current = false;
      contextGeneration.current += 1;
      uploadGeneration.current += 1;
      sequence.current += 1;
      uploadInFlight.current = false;
      pendingUploadNames.current.clear();
      updatePending(false);
    };
  }, [contextKey, updatePending]);
  useEffect(() => {
    const request = ++sequence.current;
    const isCurrent = () => request === sequence.current && activeContext.current === contextKey;
    if (!canRead || !workerId) { setDocuments([]); return; }
    setLoading(true);
    setError('');
    const result = fixtureMode ? Promise.resolve<AbsenceEvidenceDocument[]>([
      { id: 'evidence-fixture', displayName: '휴가 증빙.pdf', subjectType: 'WORKER', subjectId: workerId, scanStatus: 'CLEAN' },
    ]) : port?.list(workerId, personId) ?? Promise.resolve([]);
    void result.then((items) => {
      if (!isCurrent()) return;
      setDocuments(items); setDocumentsContext(contextKey);
      for (const item of items) knownDocuments.current.values.set(item.id, item);
      const completed = items.filter((item) => pendingUploadNames.current.has(item.id) && item.scanStatus === 'CLEAN');
      const rejected = items.filter((item) => pendingUploadNames.current.has(item.id) && item.scanStatus !== 'CLEAN' && item.scanStatus !== 'PENDING');
      if (completed.length) {
        notifySelection.current([...new Set([...selection.current, ...completed.map((item) => item.id)])]);
        setUploadStatus({ message: `${completed.map((item) => item.displayName).join(', ')} 보안 검사가 완료되어 선택했습니다.`, severity: 'success' });
      }
      if (rejected.length) setUploadError(`${rejected.map((item) => item.displayName).join(', ')} 문서를 연결할 수 없습니다. 검사 상태를 확인하거나 다른 파일을 올려 주세요.`);
      for (const item of [...completed, ...rejected]) pendingUploadNames.current.delete(item.id);
      if (completed.length || rejected.length) {
        setPendingUploads([...pendingUploadNames.current.keys()]);
        if (rejected.length && !completed.length && !pendingUploadNames.current.size) setUploadStatus(null);
        updatePending(uploadInFlight.current || pendingUploadNames.current.size > 0);
      }
    }, (failure: unknown) => {
      if (isCurrent()) setError(failure instanceof Error ? failure.message : '증빙 문서를 확인하지 못했습니다.');
    }).finally(() => { if (isCurrent()) setLoading(false); });
    return () => { sequence.current += 1; };
  }, [port, workerId, personId, fixtureMode, canRead, revision, refreshKey, contextKey, updatePending]);

  useEffect(() => {
    if (!pendingUploads.length || loading || error) return;
    const timer = window.setTimeout(() => setRevision((current) => current + 1), 3000);
    return () => window.clearTimeout(timer);
  }, [pendingUploads, loading, error, revision]);

  const currentDocuments = documentsContext === contextKey ? documents : [];
  const documentsById = new Map(currentDocuments.map((document) => [document.id, document]));
  const selectedDocuments = value.flatMap((id) => {
    const document = documentsById.get(id) ?? (knownDocuments.current.context === contextKey ? knownDocuments.current.values.get(id) : undefined);
    return document ? [document] : [];
  });
  const unknownIds = value.filter((id) => !selectedDocuments.some((document) => document.id === id));
  const selectionMessage = !value.length ? undefined
    : !canRead ? '첨부를 확인하려면 문서 조회 권한이 필요합니다.'
    : !workerId || loading || !error && documentsContext !== contextKey ? '선택한 첨부 문서를 확인하고 있습니다.'
    : error || value.some((id) => !documentsById.has(id)) ? '확인하지 못한 첨부가 있습니다. 다시 조회하거나 선택을 해제해 주세요.'
    : selectedDocuments.some((document) => document.scanStatus !== 'CLEAN') ? '보안 검사가 완료되지 않은 첨부가 있습니다. 검사 상태를 확인하거나 선택을 해제해 주세요.'
    : undefined;
  const selectionStateKey = JSON.stringify({ workerId, documentIds: value, documents: canRead ? selectedDocuments : [], ready: !selectionMessage, message: selectionMessage } satisfies AbsenceEvidenceSelection);
  useEffect(() => {
    notifySelectionState.current?.(JSON.parse(selectionStateKey) as AbsenceEvidenceSelection);
  }, [selectionStateKey]);
  const unresolvedDocuments = !loading && (error || documentsContext === contextKey)
    ? value.filter((id) => error || !documentsById.has(id) || documentsById.get(id)?.scanStatus !== 'CLEAN') : [];
  const removeSelection = (id: string) => onChange(value.filter((selected) => selected !== id));

  const cancelAttachment = () => {
    uploadGeneration.current += 1;
    uploadInFlight.current = false;
    pendingUploadNames.current.clear();
    setUploading(false); setPendingUploads([]); setUploadError('');
    setUploadStatus({ message: '신청에 연결하지 않습니다. 업로드와 검사는 계속되며, 완료된 문서는 목록에서 직접 선택할 수 있습니다.', severity: 'info' });
    updatePending(false);
    setRevision((current) => current + 1);
  };

  const upload = async (files: File[]) => {
    const file = files[0];
    if (!file || !canRead || !canUpload || disabled || uploadInFlight.current || !workerId) return;
    const generation = ++uploadGeneration.current;
    const context = contextGeneration.current;
    const isCurrentContext = () => mounted.current && contextGeneration.current === context && activeContext.current === contextKey;
    const isCurrentUpload = () => isCurrentContext() && uploadGeneration.current === generation;
    uploadInFlight.current = true;
    updatePending(true);
    setUploading(true); setUploadError('');
    setUploadStatus({ message: `${file.name} 업로드 중입니다. 보안 검사가 끝나면 자동으로 선택됩니다.`, severity: 'info' });
    try {
      let id = 'evidence-fixture';
      if (!fixtureMode) {
        if (!port) throw new Error('문서 업로드를 사용할 수 없습니다.');
        id = (await port.upload(workerId, file)).id;
      }
      if (!isCurrentUpload()) {
        // 연결을 취소한 업로드가 완료되면 자동 선택 없이 문서 목록만 갱신한다.
        if (isCurrentContext()) setRevision((current) => current + 1);
        return;
      }
      pendingUploadNames.current.set(id, file.name);
      setPendingUploads([...pendingUploadNames.current.keys()]);
      setUploadStatus({ message: `${file.name} 보안 검사 중입니다. 완료되면 자동으로 선택됩니다.`, severity: 'info' });
      setRevision((current) => current + 1);
    } catch (failure) {
      if (!isCurrentUpload()) return;
      setUploadError(failure instanceof Error ? failure.message : '증빙을 업로드하지 못했습니다. 다시 시도해 주세요.');
      setUploadStatus(null);
    } finally {
      if (isCurrentUpload()) {
        uploadInFlight.current = false;
        setUploading(false);
        updatePending(pendingUploadNames.current.size > 0);
      }
    }
  };
  if (!canRead) return <Stack spacing={1} tabIndex={-1} role="group" aria-label="증빙 문서" aria-invalid={invalid || undefined} aria-describedby={unavailableMessageId}>
    <Alert id={unavailableMessageId} severity={invalid ? 'error' : 'info'}>증빙을 선택하려면 문서 조회 권한이 필요합니다. 회사 관리자에게 문의해 주세요.</Alert>
    {value.map((id, index) => <AttachmentItem key={id} name={`확인하지 못한 첨부 ${index + 1}`} disabled={disabled} onRemove={() => removeSelection(id)} removeLabel="선택 해제" />)}
  </Stack>;
  return <Stack spacing={1.5}>
    <FormAutocomplete<AbsenceEvidenceDocument, true>
      multiple
      onBlur={onBlur}
      options={currentDocuments}
      value={selectedDocuments}
      onChange={(_, selected, reason) => onChange(reason === 'clear' ? [] : [...new Set([...unknownIds, ...selected.map((document) => document.id)])])}
      getOptionLabel={(document) => [document.displayName, document.subjectDescriptor].filter(Boolean).join(' · ')}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionDisabled={(document) => document.scanStatus !== 'CLEAN'}
      disabled={disabled || loading || uploading || !workerId}
      request={error ? { status: 'error', message: error, onRetry: () => setRevision((current) => current + 1) } : { status: loading ? 'loading' : 'ready' }}
      selectionError={!loading && value.length ? selectionMessage : undefined}
      input={{ label: '증빙 문서', required, error: invalid, placeholder: '문서 이름으로 검색', helperText: helperText ?? '보안 검사를 통과한 문서만 연결할 수 있습니다.', help: '휴가를 신청하는 구성원의 문서를 선택하세요. 새로 첨부한 문서는 보안 검사가 끝나면 자동으로 선택됩니다.' }}
      renderOption={({ key, ...props }, document) => <Box component="li" key={key} {...props}><Stack>
        <Typography variant="body2">{document.displayName}</Typography>
        <Typography variant="caption" color="text.secondary">{[document.subjectDescriptor, document.scanStatus === 'CLEAN' ? '연결 가능' : document.scanStatus === 'PENDING' ? '보안 검사 중' : '연결 불가'].filter(Boolean).join(' · ')}</Typography>
      </Stack></Box>}
    />
    {unresolvedDocuments.map((id, index) => {
      const document = selectedDocuments.find((selected) => selected.id === id);
      const currentDocument = documentsById.get(id);
      return <AttachmentItem key={id} name={document?.displayName ?? `확인하지 못한 첨부 ${index + 1}`}
        description={error || !currentDocument ? '확인하지 못한 첨부' : currentDocument.scanStatus === 'PENDING' ? '보안 검사 중' : '연결 불가'}
        disabled={disabled} onRemove={() => removeSelection(id)} removeLabel="선택 해제" />;
    })}
    {canUpload && <AttachmentField label="새 증빙 첨부" actionLabel="파일 선택" onFilesSelected={(files) => { void upload(files); }} disabled={disabled || !workerId} busy={uploading} error={uploadError || undefined} inputTestId="absence-evidence-file" />}
    {uploadStatus && <Alert severity={uploadStatus.severity} role="status">{uploadStatus.message}</Alert>}
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      <Button size="small" disabled={disabled || loading || uploading} onClick={() => setRevision((current) => current + 1)}>검사 상태 새로고침</Button>
      {(uploading || pendingUploads.length > 0) && <Button size="small" disabled={disabled} onClick={cancelAttachment}>첨부 취소</Button>}
    </Stack>
  </Stack>;
}
